import React, { useRef, useState } from 'react';
import { Button, makeStyles, FormControl, FormLabel, Typography, Input, Grid, TextField } from '@material-ui/core';
import Dialog from '../../ui/Dialog';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay.component';
import { updateTransactionLineItem } from '../../../services/transaction.service';
import CreateIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { upload }  from '../../../services/storage.service';
import moment from 'moment';
import { voidTransactionLineItem } from '../../../services/transaction.service';

const UpdateLineItem = ({ data }) => {
  const { transaction_line_item_id, quantity, receipt_image, receipt_date, program_id, 
    patient_id, reason_for_voiding } = data;
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openVoidModal, setOpenVoidModal] = useState(false);
  const [openConfirmation, setConfirmation] = useState(false);
  const [modalTitle, setModalTitle] = useState(null);
  const [lineItemDetails, setLineItemDetails] = useState({
    quantity: quantity,
    receipt_image: receipt_image,
    receipt_date: receipt_date,
    reason: reason_for_voiding,
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState('Line item successfully updated!');
  const [isLoading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  let inputFileRef = useRef(null);

  const useStyles = makeStyles(() => ({
    field: {
      marginBottom: 20,
    },
    formControl: {
      width: '100%'
    }
  }));
  const classes = useStyles();
  
  const handleClickAction = () => {
    setOpenUpdateModal(true);
    setModalTitle('Update Line Item Details');
  }

  const handleVoidAction = async () => {
    setOpenVoidModal(true);
    setModalTitle('Void Line Item');
  };

  const onVoidSubmit = async () => {
    setLoading(true);
    await voidTransactionLineItem({
        connect_program_id: program_id,
        transaction_line_item_id,
        payload: {
          reason_for_voiding: lineItemDetails.reason,
        }
    })
    .then((res) => {
        setSuccessMessage('Line item successfully voided!');
        handleClose(true);
    })
    .catch((err) => alert(err.message))
    .finally(() => {
        setLoading(false);
    })
  }

  const handleClose = (reload) => {
    setOpenUpdateModal(false);
    setOpenVoidModal(false);
    setLineItemDetails({
        quantity: quantity,
        receipt_image: receipt_image,
        receipt_date: receipt_date,
        reason: reason_for_voiding,
    });
    setConfirmation(false);
    setErrorMessage(null);
    if (openConfirmation || reload) {
      window.location.reload();
    }
  }

  const blobToFile = (imageBlob) => {
    return new File([imageBlob], imageBlob.name, {
        lastModified: moment().unix(),
        type: imageBlob.type,
    });
  }
 
  const onSubmit = async () => {
    try {
      setLoading(true);

      let imgUrl = undefined;
      if (selectedImage) {
        const { result } = await upload(blobToFile(selectedImage), program_id);
        imgUrl = result.url;
      }
      await updateTransactionLineItem(
        {
          connect_program_id: program_id,
          transaction_line_item_id,
          payload: {
            quantity: parseInt(lineItemDetails.quantity.toString()),
            campaign_metadata: {
              receipt: imgUrl,
              receipt_date: lineItemDetails.receipt_date,
            }
          },
        });

        setOpenUpdateModal(false);
        setConfirmation(true);
        setSuccessMessage('Line item successfully updated!');
    } catch(err) {
      setErrorMessage('Unable to update line item');
    } finally {
      setLoading(false);
    }
  }

  const onFieldInput = (e) => {
    const { name, value } = e.target;

    setLineItemDetails(prevState => ({
      ...prevState,
      [name]: value,
    }));
  }

  const updateLineItemFormContent = (
    <>
      <FormControl className={classes.formControl} component={'span'}>
          <FormLabel >
          <Typography>
              Quantity
          </Typography>
          </FormLabel>
          <Input className={classes.field} component={'span'}
            required={true} 
            name='quantity'
            type='text' 
            defaultValue={quantity}  
            onChange={onFieldInput}
          />
      </FormControl>
      <FormControl className={classes.formControl} component={'span'}>
        <FormLabel>
          <Typography component={'span'}>
              Receipt Image
          </Typography>
        </FormLabel>
        {
          lineItemDetails.receipt_image && !selectedImage && 
          <>
            <Grid container>
              <Grid item sm={8}>
                <img width={"250px"} src={lineItemDetails.receipt_image} /> 
              </Grid>
              <Grid item sm={4}>
                <Button color="primary" variant="contained" onClick={()=> { setLineItemDetails({ ...lineItemDetails, receipt_image: null }) } }>Remove Image</Button>
              </Grid>
            </Grid>
          </>
        }
        {
          selectedImage && 
          <>
            <Grid container>
              <Grid item sm={8}>
                <img width={"250px"} src={window.URL.createObjectURL(new Blob([selectedImage]))} /> 
              </Grid>
              <Grid item sm={4}>
                <Button color="primary" variant="contained" onClick={()=> { setSelectedImage(null) } }>Remove Image</Button>
              </Grid>
            </Grid>
          </>
        }
        <Button color="primary" variant="contained" onClick={()=> { inputFileRef.current.click() } }>Upload Image</Button>
        <Input component={'span'} style={{ visibility: 'hidden' }} type="file" id="file" inputRef={inputFileRef} onChange={async (event) => {
          setSelectedImage(event.target.files[0]);
        }}></Input>
      </FormControl>
      <FormControl className={classes.formControl} component={'span'}>
        <FormLabel>
          <Typography component={'span'}>
              Receipt Date
          </Typography>
        </FormLabel>
        <Input className={classes.field} component={'span'}
            required={true} 
            name='receipt_date'
            type='date' 
            defaultValue={receipt_date}  
            onChange={onFieldInput}
          />
      </FormControl>
      {/* Error Message */}
      <Typography
          variant='caption'
          display='block'
          gutterBottom
          color='error'
      >
          { errorMessage }
      </Typography>
    </>
  );

  const voidLineItemFormContent = (
    <>
      <FormControl className={classes.formControl} component={'span'}>
          <FormLabel >
          <Typography>
              Reason for Voiding
          </Typography>
          </FormLabel>
          <TextField className={classes.field} component={'span'}
              defaultValue={lineItemDetails.reason}
              multiline={true}
              rows={4}
              label='Reason'
              required={true}
              name='reason' 
              variant='outlined'
              onChange={onFieldInput} 
          />
      </FormControl>
      {/* Error Message */}
      <Typography
          variant='caption'
          display='block'
          gutterBottom
          color='error'
      >
          { errorMessage }
      </Typography>
    </>
  );

  const formButtons = ({ onSubmitAction = onSubmit, submitButtonText = 'Submit' }) => (
    <>
        <Button onClick={() => handleClose(false)} variant='contained'>
            Close
        </Button>
        { !openConfirmation && 
        <Button onClick={onSubmitAction} variant='contained' color='secondary'>
          { submitButtonText }
        </Button> }
    </>
  );

  const confirmationButtons = (
    <>
      <Button onClick={() => handleClose(true)} variant='contained'>
        Close
      </Button>
    </>
  );

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <>
      <Dialog
        key='void-line-item'
        open={openVoidModal}
        title = {modalTitle}
        content = {voidLineItemFormContent} 
        children = {formButtons({ onSubmitAction: onVoidSubmit, submitButtonText: 'Void' })} 
        onSubmit = {onVoidSubmit}
        onClose = {() => handleClose(false)} 
      />
     <Dialog 
      key='update-line-item'
      open={openUpdateModal} 
      title = {modalTitle}
      content = {updateLineItemFormContent}
      children = {formButtons({ onSubmitAction: onSubmit, submitButtonText: 'Submit' })}
      onSubmit={onSubmit}
      onClose={() => handleClose(false)}
      />
     <Dialog 
      key='confirmation'
      open={openConfirmation}
      title = 'Confirmation'
      content = {successMessage}
      children = {confirmationButtons}
      onSubmit={() => handleClose(true)}
      onClose={() => handleClose(true)}
      />
     <Button variant="contained" color='primary' onClick={handleClickAction} style={{ marginRight: 8 }}>
      <EditIcon></EditIcon>
     </Button>
     <Button variant="contained" color='secondary' onClick={handleVoidAction} style={{ marginRight: 8 }}>
      <DeleteIcon></DeleteIcon>
     </Button>
    </>
  )
};

export default UpdateLineItem;